/**
 * Shared server-side pagination contract for list endpoints.
 *
 * The backend `PaginatesResults` trait emits camelCase meta
 * ({ total, perPage, currentPage, lastPage, from, to, hasNextPage }).
 * A few older endpoints (e.g. customer detail sub-lists) use Laravel's
 * default paginator, which serializes snake_case — {@link mapPaginationMeta}
 * normalizes either shape.
 */

/** Standardized pagination metadata. */
export interface PaginationMeta {
  total: number;
  perPage: number;
  currentPage: number;
  lastPage: number;
  from: number | null;
  to: number | null;
  hasNextPage: boolean;
}

/** A single page of results together with its metadata. */
export interface Paginated<T> {
  items: T[];
  meta: PaginationMeta;
}

/** Raw meta as it may arrive from the API, in either casing. */
export interface RawPaginationMeta {
  total?: number;
  perPage?: number;
  per_page?: number;
  currentPage?: number;
  current_page?: number;
  lastPage?: number;
  last_page?: number;
  from?: number | null;
  to?: number | null;
  hasNextPage?: boolean;
}

/** Query knobs every paginated list request shares. */
export interface PaginationQuery {
  page?: number;
  perPage?: number;
}

/** Normalize raw API meta (camelCase or snake_case) into a PaginationMeta. */
export function mapPaginationMeta(
  meta?: RawPaginationMeta,
  fallbackCount = 0,
): PaginationMeta {
  const currentPage = meta?.currentPage ?? meta?.current_page ?? 1;
  const lastPage = meta?.lastPage ?? meta?.last_page ?? 1;

  return {
    total: meta?.total ?? fallbackCount,
    perPage: meta?.perPage ?? meta?.per_page ?? 25,
    currentPage,
    lastPage,
    from: meta?.from ?? null,
    to: meta?.to ?? null,
    hasNextPage: meta?.hasNextPage ?? currentPage < lastPage,
  };
}
